import { useEffect, useRef } from "react";
import { useSubscription } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { gql } from "@lytra/frontend-common/__generated__/gql";
import { assignedInThisTab } from "./useConversationChange";

// A ticket's owner as pushed live: Apollo writes each push into the cached conversation,
// so the open ticket's header, list row and panel follow an owner change made anywhere,
// whatever the list is filtered to.
const TICKET_OWNER_UPDATES = gql(`
  subscription TicketOwnerUpdates {
    conversations {
      id
      assignedAgent {
        id
        givenName
        familyName
      }
      assignedTeam {
        id
        name
      }
      assignedById
    }
  }
`);

/** Keep the cached owner of every ticket current while `enabled`. */
export function useTicketOwnerUpdates(enabled: boolean): void {
  useSubscription(TICKET_OWNER_UPDATES, { skip: !enabled });
}

/** Whether a key press is the plain letter, outside any text field. */
const isPlainKey = (event: KeyboardEvent, key: string): boolean => {
  if (event.key.toLowerCase() !== key) return false;
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  const target = event.target as HTMLElement | null;
  if (!target) return true;
  const tag = target.tagName;
  return !(
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
};

/** "I" assigns the open ticket to the viewer, unless typing somewhere. `assignToMe` is
 * null while there is nothing to do (already the viewer's). */
export function useAssignToMeShortcut(assignToMe: (() => void) | null): void {
  useEffect(() => {
    if (!assignToMe) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isPlainKey(event, "i") || event.repeat) return;
      event.preventDefault();
      assignToMe();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [assignToMe]);
}

interface OwnershipToastsArgs {
  enabled: boolean;
  conversationId: string;
  ownerId: string | null;
  /** Who made the latest owner change (ticket history). */
  assignedById: string | null;
  currentUserId: string;
  nameOf: (userId: string) => string | null;
  /** Gives the ticket back to `previousOwnerId` (null: nobody). */
  reassign: (previousOwnerId: string | null) => void;
}

/**
 * The toasts of an owner change of the open ticket that this tab did not make:
 * - nobody's to mine, by me: my first answering action claimed it ("Dir zugewiesen", undo);
 * - to mine, by a colleague: "Anna hat dir das Ticket zugewiesen";
 * - mine to a colleague who took it: "Markus hat dieses Ticket übernommen".
 * A later one replaces the toast of the one before on the same ticket.
 */
export function useOwnershipToasts({
  enabled,
  conversationId,
  ownerId,
  assignedById,
  currentUserId,
  nameOf,
  reassign,
}: OwnershipToastsArgs): void {
  const { t } = useTranslation();
  const previousRef = useRef<{
    conversationId: string;
    ownerId: string | null;
  } | null>(null);
  // Read inside the effect below without re-running it when only these change. Declared
  // first, so it is current when that effect runs.
  const latestRef = useRef({ assignedById, nameOf, reassign });
  useEffect(() => {
    latestRef.current = { assignedById, nameOf, reassign };
  });

  useEffect(() => {
    const before = previousRef.current;
    previousRef.current = { conversationId, ownerId };
    if (!enabled || before?.conversationId !== conversationId) return;
    if (before.ownerId === ownerId || assignedInThisTab(conversationId)) return;
    const {
      assignedById: by,
      nameOf: name,
      reassign: giveBack,
    } = latestRef.current;
    const priorOwner = before.ownerId;
    // One toast per ticket: a later owner change replaces the earlier one's toast, so an
    // undone takeover leaves one toast, not two.
    const id = `ticket-ownership:${conversationId}`;
    if (ownerId === currentUserId) {
      if (by === currentUserId && priorOwner === null) {
        toast(t("tickets.ownership.assignedToYou"), {
          id,
          description: t("tickets.ownership.assignedToYouHint"),
          action: {
            label: t("tickets.ownership.undo"),
            onClick: () => giveBack(null),
          },
        });
      } else if (by && by !== currentUserId) {
        toast(
          t("notifications.reason.ASSIGNED", {
            actor: name(by) ?? t("tickets.ownership.someone"),
          }),
          { id },
        );
      }
    } else if (priorOwner === currentUserId && ownerId && by === ownerId) {
      toast(
        t("tickets.ownership.tookOver", {
          name: name(ownerId) ?? t("tickets.ownership.someone"),
        }),
        { id },
      );
    }
  }, [enabled, conversationId, ownerId, currentUserId, t]);
}
